import React from 'react';
import { createPortal } from 'react-dom';
import { CommentsHeader } from '../comments-header/comments-header';
import { resolveHeaderVisibility, resolveUserAuthControlState, } from '../comments-header/comments-header-utils';
import { getResourceCommentsState } from '../../../common/store/comments/comments-selectors';
import { useApi } from '../../context/api-context/use-api';
import { isLoggingEnabled, getIsSSR, isDebug, } from '../../../common/store/base-params/base-params-selectors';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { defaultPaginationConfig } from '../../../common/constants/config-defaults';
import { unreachable } from '../../../ts-utils';
import ListLoadingState from '../../empty-states/list-loading-state';
import DebugButton from '../dev-panel/debug-button';
import AppReady from './app-ready';
import OverlayLayerContainer from './overlay-layer-container';
import { getCurrentMember } from '../../../common/store/members/members-selectors';
import CommentNotFoundState from '../../empty-states/comment-not-found-state';
import DefaultListFetchErrorState from '../../empty-states/default-list-fetch-error-state';
const App = React.memo(() => {
    const { resourceId, pagination = defaultPaginationConfig, ctxFields, deepLink, hideHeader, hideTitle, reactions, disableResourceAutoFetching = false, disableResourceCleanup = false, ratings, userAuthDisplay, } = useConfiguration();
    const { fetchComments, fetchDeepLink, resourceComponentMounted, resourceComponentUnmounted, resourceState, shouldLog, isSSR, currentMember, } = useApi((state, actions) => ({
        fetchComments: actions.fetchComments,
        fetchDeepLink: actions.fetchDeepLink,
        resourceComponentMounted: actions.resourceComponentMounted,
        resourceComponentUnmounted: actions.resourceComponentUnmounted,
        shouldLog: isLoggingEnabled(state),
        isSSR: getIsSSR(state),
        resourceState: state.comments[resourceId],
        currentMember: getCurrentMember(state),
    }));
    const listState = getResourceCommentsState(resourceState);
    const withVotes = reactions?.commentReactions?.type === 'votes';
    const withRatings = Boolean(ratings);
    if (shouldLog) {
        console.log('[Comments list state]', listState);
    }
    React.useEffect(() => {
        if (disableResourceAutoFetching) {
            return;
        }
        if (deepLink?.commentId) {
            fetchDeepLink({
                ctxFields,
                commentId: deepLink.commentId,
                resourceId,
                pagination,
                withVotes,
                withRatings,
            });
        }
        else {
            fetchComments({
                resourceId,
                pagination,
                ctxFields,
                withVotes,
                withRatings,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceId, deepLink?.commentId, disableResourceAutoFetching]);
    React.useEffect(() => {
        resourceComponentMounted({ resourceId, disableResourceCleanup });
        return () => resourceComponentUnmounted({ resourceId, disableResourceCleanup });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceId, disableResourceCleanup]);
    React.useEffect(() => {
        // we check if it's not SSR, because if it is true, then it means this is initial hydration with SSR props and there is no controller yet
        if (!isSSR) {
            if (typeof performance !== 'undefined') {
                performance.mark && performance.mark('wc-comments-rendered');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const userAuthControlState = React.useMemo(() => resolveUserAuthControlState(userAuthDisplay, currentMember), [userAuthDisplay, currentMember]);
    switch (listState.type) {
        case 'ERROR': {
            const headerVisibility = resolveHeaderVisibility({
                hideHeader,
                hideRatings: !withRatings,
                hideTitle,
                userAuthControlState,
            });
            const onRetry = () => {
                fetchComments({
                    resourceId,
                    pagination,
                    ctxFields,
                    withVotes,
                    withRatings,
                });
            };
            const mainComponent = {
                FAILED: React.createElement(DefaultListFetchErrorState, { onRetry: onRetry }),
                COMMENT_NOT_FOUND: React.createElement(CommentNotFoundState, { onRetry: onRetry }),
            };
            return (React.createElement(Container, null,
                headerVisibility.type === 'VISIBLE' && (React.createElement(CommentsHeader, { ratings: { type: 'DISABLED' }, ...headerVisibility.visibilityProps })),
                mainComponent[listState.subType]));
        }
        case 'PENDING': {
            const headerVisibility = resolveHeaderVisibility({
                hideHeader,
                hideRatings: !withRatings,
                hideTitle,
                userAuthControlState,
            });
            return (React.createElement(Container, null,
                headerVisibility.type === 'VISIBLE' && (React.createElement(CommentsHeader, { commentCount: listState.lastTotals === undefined ? undefined : listState.lastTotals, ratings: { type: 'DISABLED' }, ...headerVisibility.visibilityProps })),
                React.createElement(ListLoadingState, null)));
        }
        case 'READY': {
            const headerVisibility = resolveHeaderVisibility({
                hideHeader,
                hideRatings: !withRatings,
                hideTitle,
                userAuthControlState,
            });
            return (React.createElement(Container, null,
                headerVisibility.type === 'VISIBLE' && (React.createElement(CommentsHeader, { commentCount: listState.totals, ratings: {
                        type: 'ENABLED',
                        total: listState.ratings.total,
                        average: listState.ratings.average,
                    }, ...headerVisibility.visibilityProps })),
                React.createElement(AppReady, { listState: listState, appHeaderIsVisible: headerVisibility.type === 'VISIBLE' })));
        }
        default:
            throw unreachable(listState);
    }
});
App.displayName = 'App';
const Container = ({ children }) => {
    const showDebugButton = useApi((state) => isDebug(state));
    const { overlayLayerContainer } = useConfiguration();
    return (React.createElement(React.Fragment, null,
        showDebugButton && React.createElement(DebugButton, null),
        overlayLayerContainer ? (createPortal(React.createElement(OverlayLayerContainer, null), overlayLayerContainer)) : (React.createElement(OverlayLayerContainer, null)),
        children));
};
export default App;
//# sourceMappingURL=app.js.map